import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { SERVICES, CLIENT_TYPES, CONTACT_METHODS } from "../data/content";

const initial = {
  name: "", mobile: "", email: "", city: "",
  client_type: "Individual", service: "", contact_method: "Phone Call",
  preferred_date: "", message: "", consent: false, website: "",
};

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition";

export function EnquiryForm({ compact = false }) {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const svc = params.get("service");
    if (svc) {
      const match = SERVICES.find((s) => s.slug === svc || s.title === svc);
      if (match) setForm((f) => ({ ...f, service: match.title }));
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the privacy consent to submit your enquiry.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, preferred_date: form.preferred_date || null });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to submit right now. Please call or WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-gold/40 bg-white p-10 text-center" data-testid="enquiry-success-message">
        <CheckCircle2 size={48} className="mx-auto text-gold" />
        <h3 className="mt-5 font-serif text-2xl font-semibold text-navy">Thank You</h3>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
          Thank you for contacting Neeraj Battan &amp; Associates. Our office will review your enquiry and contact you shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 shadow-xl shadow-navy/5" data-testid="enquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Full Name *</label>
          <input required minLength={2} className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="enquiry-name-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Mobile Number *</label>
          <input required className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91 XXXXX XXXXX" data-testid="enquiry-mobile-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Email Address *</label>
          <input required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="enquiry-email-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">City *</label>
          <input required minLength={2} className={inputCls} value={form.city} onChange={set("city")} placeholder="Your city" data-testid="enquiry-city-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Client Type *</label>
          <select required className={inputCls} value={form.client_type} onChange={set("client_type")} data-testid="enquiry-client-type-select">
            {CLIENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Required Service *</label>
          <select required className={inputCls} value={form.service} onChange={set("service")} data-testid="enquiry-service-select">
            <option value="" disabled>Select a service</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="Other / General Enquiry">Other / General Enquiry</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Preferred Contact Method *</label>
          <select required className={inputCls} value={form.contact_method} onChange={set("contact_method")} data-testid="enquiry-contact-method-select">
            {CONTACT_METHODS.map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Preferred Consultation Date</label>
          <input type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} data-testid="enquiry-date-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Message</label>
          <textarea rows={compact ? 3 : 4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Briefly describe your requirement" data-testid="enquiry-message-input" />
        </div>
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden="true"
          value={form.website}
          onChange={set("website")}
          data-testid="enquiry-honeypot"
        />
        <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="enquiry-consent-label">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            className="mt-1 w-4 h-4 accent-[#C5A059]"
            data-testid="enquiry-consent-checkbox"
          />
          <span className="text-xs text-slate-500 leading-relaxed">
            I consent to Neeraj Battan &amp; Associates contacting me regarding this enquiry and storing the details provided, as described in the Privacy Policy.
          </span>
        </label>
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-form-submit-button"
        className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-60 transition-colors"
      >
        {submitting ? "Submitting…" : "Submit Enquiry"}
        <Send size={16} className="text-gold" />
      </button>
    </form>
  );
}
